// src/pages/player/QuizPage.jsx
import React, {useEffect, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {get, push, ref, serverTimestamp, set, update} from 'firebase/database';
import {database} from '../../services/firebase';
import {useAuth} from '../../contexts/AuthContext';
import Countdown from 'react-countdown';
import Header from "../../components/common/Header";
import {biblicalBoooksAndCategories} from "../../utils/utils";

const {categories, biblicalBooks} = biblicalBoooksAndCategories();

export default function QuizPage() {
    const {quizId} = useParams();
    const {currentUser} = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const isTrainingMode = location.pathname.includes('/training/session');

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [quiz, setQuiz] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [selectedOption, setSelectedOption] = useState(null);
    const [quizResults, setQuizResults] = useState({
        score: 0,
        correctAnswers: 0,
        answers: []
    });
    const [timePerQuestion, setTimePerQuestion] = useState(30); // Défaut: 30 secondes
    const [timeRemaining, setTimeRemaining] = useState(30000); // En millisecondes
    const [countdownKey, setCountdownKey] = useState(0);
    const [showExplanation, setShowExplanation] = useState(false);
    const [timerRunning, setTimerRunning] = useState(true);
    const [startTime, setStartTime] = useState(null);
    const [gameSessionId, setGameSessionId] = useState(null);

    // Récupérer les informations du quiz ou préparer les questions d'entraînement
    useEffect(() => {
        async function fetchQuizData() {
            try {
                if (isTrainingMode) {
                    // En mode entraînement, récupérer la configuration et générer un quiz personnalisé
                    const trainingConfigStr = sessionStorage.getItem('trainingConfig');
                    if (!trainingConfigStr) {
                        setError('Configuration d\'entraînement introuvable. Veuillez retourner à la page d\'entraînement.');
                        setLoading(false);
                        return;
                    }

                    const trainingConfig = JSON.parse(trainingConfigStr);

                    // Récupérer les questions correspondant aux critères (catégories, livres, difficulté)
                    const questionsRef = ref(database, 'questionBank');
                    const questionsSnapshot = await get(questionsRef);
                    const questionsData = questionsSnapshot.val() || {};

                    // Filtrer les questions selon les critères
                    let filteredQuestions = Object.entries(questionsData).map(([id, data]) => ({
                        id,
                        ...data
                    }));

                    // Appliquer les filtres de catégorie si spécifiés
                    if (trainingConfig.categories && trainingConfig.categories.length > 0) {
                        filteredQuestions = filteredQuestions.filter(q => {
                                if (q.category) {
                                    const cat = categories.find(b => b.name === q.category);
                                    return trainingConfig.categories.includes(cat.id)
                                } else {
                                    return false;
                                }
                            }
                        );
                    }

                    // Appliquer les filtres de livre si spécifiés
                    if (trainingConfig.books && trainingConfig.books.length > 0) {
                        filteredQuestions = filteredQuestions.filter(q => {
                                if (q.bookReference) {
                                    const book = biblicalBooks.find(b => b.name === q.bookReference);
                                    return trainingConfig.books.includes(book.id);
                                } else {
                                    return false;
                                }
                            }
                        );
                    }

                    // Appliquer le filtre de difficulté si spécifié et différent de "mixed"
                    if (trainingConfig.difficulty && trainingConfig.difficulty !== 'mixed') {
                        filteredQuestions = filteredQuestions.filter(q =>
                            q.difficulty === trainingConfig.difficulty
                        );
                    }

                    // Récupérer les questions récemment vues pour les éviter si possible
                    const recentQuestionsRef = ref(database, `users/${currentUser.uid}/recentQuestions`);
                    const recentQuestionsSnapshot = await get(recentQuestionsRef);
                    const recentQuestions = recentQuestionsSnapshot.val() || [];

                    // Exclure les questions récentes si possible
                    let availableQuestions = filteredQuestions.filter(q => !recentQuestions.includes(q.id));

                    // Si pas assez de questions disponibles, utiliser toutes les questions filtrées
                    if (availableQuestions.length < trainingConfig.numQuestions) {
                        availableQuestions = filteredQuestions;
                    }

                    // Mélanger et prendre le nombre requis
                    const shuffled = [...availableQuestions].sort(() => 0.5 - Math.random());
                    const selectedQuestions = shuffled.slice(0, trainingConfig.numQuestions);

                    // Créer un quiz d'entraînement
                    setQuiz({
                        metadata: {
                            title: 'Session d\'entraînement personnalisée',
                            description: 'Quiz généré automatiquement selon vos préférences',
                            timePerQuestion: 30,
                            difficulty: trainingConfig.difficulty,
                            totalQuestions: selectedQuestions.length,
                            isTraining: true
                        }
                    });

                    setQuestions(selectedQuestions);
                    setTimePerQuestion(30);

                    // Créer une session d'entraînement
                    const sessionRef = ref(database, `gameResults/training/${currentUser.uid}`);
                    const newSessionRef = push(sessionRef);
                    const sessionId = newSessionRef.key;

                    await set(newSessionRef, {
                        startedAt: serverTimestamp(),
                        config: trainingConfig,
                        playerData: {
                            displayName: currentUser.displayName,
                            totalQuestions: selectedQuestions.length
                        }
                    });

                    setGameSessionId(sessionId);

                } else {
                    // En mode quiz régulier, charger le quiz depuis la base de données
                    const quizRef = ref(database, `quizzes/${quizId}`);
                    const quizSnapshot = await get(quizRef);

                    if (!quizSnapshot.exists()) {
                        setError('Quiz introuvable.');
                        setLoading(false);
                        return;
                    }

                    const quizData = quizSnapshot.val();
                    setQuiz(quizData);
                    setQuestions(quizData.questions || []);
                    setTimePerQuestion(quizData.metadata.timePerQuestion || 30);

                    // Créer une session de jeu
                    const sessionRef = ref(database, `gameResults/${quizId}`);
                    const newSessionRef = push(sessionRef);
                    const sessionId = newSessionRef.key;

                    await set(newSessionRef, {
                        quizData: {
                            title: quizData.metadata.title,
                            timePerQuestion: quizData.metadata.timePerQuestion
                        },
                        startedAt: serverTimestamp(),
                        players: {
                            [currentUser.uid]: {
                                displayName: currentUser.displayName,
                                score: 0,
                                questionsCorrect: 0,
                                totalQuestions: quizData.questions.length
                            }
                        },
                        individualResponses: {
                            [currentUser.uid]: {}
                        }
                    });

                    setGameSessionId(sessionId);
                }

                setStartTime(Date.now());
                setTimeRemaining(timePerQuestion * 1000);
                setLoading(false);
            } catch (error) {
                console.error("Erreur lors du chargement du quiz:", error);
                setError("Une erreur s'est produite lors du chargement du quiz.");
                setLoading(false);
            }
        }

        if (currentUser) {
            fetchQuizData();
        } else {
            setError("Vous devez être connecté pour jouer.");
            setLoading(false);
        }
    }, [currentUser, quizId, isTrainingMode, timePerQuestion]);

    // Soumettre une réponse et passer à la question suivante
    const handleAnswerSubmit = async () => {
        if (loading) return;

        const currentQuestion = questions[currentQuestionIndex];
        const isCorrect = selectedOption === currentQuestion.correctAnswer;
        const responseTime = (Date.now() - startTime) / 1000; // Temps de réponse en secondes

        // Mettre à jour les résultats du quiz
        const updatedResults = {
            ...quizResults,
            score: quizResults.score + (isCorrect ? currentQuestion.points || 1 : 0),
            correctAnswers: quizResults.correctAnswers + (isCorrect ? 1 : 0),
            answers: [
                ...quizResults.answers,
                {
                    questionId: currentQuestion.id,
                    selectedOption,
                    correct: isCorrect,
                    responseTime
                }
            ]
        };

        setQuizResults(updatedResults);

        // Sauvegarder la réponse dans la base de données
        try {
            if (isTrainingMode) {
                // Mise à jour de la session d'entraînement
                const responseRef = ref(database, `gameResults/training/${currentUser.uid}/${gameSessionId}/responses/${currentQuestionIndex}`);
                await set(responseRef, {
                    questionId: currentQuestion.id,
                    selectedOption,
                    correct: isCorrect,
                    responseTime
                });

                // Mettre à jour les statistiques d'entraînement
                const statsRef = ref(database, `users/${currentUser.uid}/trainingStats`);
                const statsSnapshot = await get(statsRef);
                const currentStats = statsSnapshot.val() || {
                    questionsAnswered: 0,
                    correctAnswers: 0,
                    categoryPerformance: {},
                    bookPerformance: {}
                };

                if (currentStats.categoryPerformance === undefined) {
                    currentStats.categoryPerformance = {};
                }
                if (currentStats.bookPerformance === undefined) {
                    currentStats.bookPerformance = {};
                }

                // Mettre à jour les performances globales
                const updatedStats = {
                    questionsAnswered: currentStats.questionsAnswered + 1,
                    correctAnswers: currentStats.correctAnswers + (isCorrect ? 1 : 0)
                };

                // Mettre à jour les performances par catégorie
                const category = currentQuestion.category;
                console.log(`Current stats : ${currentStats}`)
                console.table(currentStats)
                const categoryStats = currentStats.categoryPerformance[category] || {
                    asked: 0,
                    correct: 0,
                    percentSuccess: 0
                };
                updatedStats.categoryPerformance = {
                    ...currentStats.categoryPerformance,
                    [category]: {
                        asked: categoryStats.asked + 1,
                        correct: categoryStats.correct + (isCorrect ? 1 : 0),
                        percentSuccess: ((categoryStats.correct + (isCorrect ? 1 : 0)) / (categoryStats.asked + 1) * 100).toFixed(2)
                    }
                };

                // Mettre à jour les performances par livre
                if (currentQuestion.bookReference) {
                    const book = currentQuestion.bookReference;
                    const bookStats = currentStats.bookPerformance[book] || {asked: 0, correct: 0, percentSuccess: 0};
                    updatedStats.bookPerformance = {
                        ...currentStats.bookPerformance,
                        [book]: {
                            asked: bookStats.asked + 1,
                            correct: bookStats.correct + (isCorrect ? 1 : 0),
                            percentSuccess: ((bookStats.correct + (isCorrect ? 1 : 0)) / (bookStats.asked + 1) * 100).toFixed(2)
                        }
                    };
                }

                await update(statsRef, updatedStats);

                // Ajouter la question aux questions récentes
                const recentQuestionsRef = ref(database, `users/${currentUser.uid}/recentQuestions`);
                const recentQuestionsSnapshot = await get(recentQuestionsRef);
                const recentQuestions = recentQuestionsSnapshot.val() || [];

                // Garder les 50 dernières questions
                const updatedRecentQuestions = [currentQuestion.id, ...recentQuestions].slice(0, 50);
                await set(recentQuestionsRef, updatedRecentQuestions);

            } else {
                // Mise à jour de la session de jeu normale
                const responseRef = ref(database, `gameResults/${quizId}/${gameSessionId}/individualResponses/${currentUser.uid}/${currentQuestionIndex}`);
                await set(responseRef, {
                    selectedOption,
                    correct: isCorrect,
                    responseTime
                });

                // Mettre à jour le score du joueur
                const playerRef = ref(database, `gameResults/${quizId}/${gameSessionId}/players/${currentUser.uid}`);
                await update(playerRef, {
                    score: updatedResults.score,
                    questionsCorrect: updatedResults.correctAnswers
                });
            }
        } catch (error) {
            console.error("Erreur lors de l'enregistrement de la réponse:", error);
        }

        // Afficher l'explication pendant quelques secondes
        setShowExplanation(true);
        setTimerRunning(false);

        setTimeout(() => {
            // Passer à la question suivante
            if (currentQuestionIndex < questions.length - 1) {
                setCurrentQuestionIndex(prevIndex => prevIndex + 1);
                setSelectedOption(null);
                setShowExplanation(false);
                setStartTime(Date.now());
                setTimeRemaining(timePerQuestion * 1000);
                setTimerRunning(true);
                setCountdownKey(prev => prev + 1);
            } else {
                // Fin du quiz
                finishQuiz();
            }
        }, 3000); // Afficher l'explication pendant 3 secondes
    };

    // Gestion de la fin du chronomètre
    function handleTimerComplete() {
        setTimerRunning(false);
        if (selectedOption === null) {
            handleAnswerSubmit();
        }
    }

    // Terminer le quiz et enregistrer les résultats
    const finishQuiz = async () => {
        try {
            if (isTrainingMode) {
                // Mise à jour de la session d'entraînement
                const sessionRef = ref(database, `gameResults/training/${currentUser.uid}/${gameSessionId}`);
                await update(sessionRef, {
                    endedAt: serverTimestamp(),
                    results: {
                        score: quizResults.score,
                        correctAnswers: quizResults.correctAnswers,
                        totalQuestions: questions.length,
                        accuracy: ((quizResults.correctAnswers / questions.length) * 100).toFixed(2)
                    }
                });

            } else {
                // Mise à jour de la session de jeu normale
                const sessionRef = ref(database, `gameResults/${quizId}/${gameSessionId}`);
                await update(sessionRef, {
                    endedAt: serverTimestamp()
                });

                // Mettre à jour l'historique de jeu de l'utilisateur
                const historyRef = ref(database, `users/${currentUser.uid}/gameHistory`);
                const historySnapshot = await get(historyRef);
                const currentHistory = historySnapshot.val() || {totalGames: 0, totalScore: 0};

                await update(historyRef, {
                    totalGames: currentHistory.totalGames + 1,
                    totalScore: currentHistory.totalScore + quizResults.score,
                    lastPlayed: serverTimestamp()
                });

                // Mettre à jour le classement
                const leaderboardRef = ref(database, `leaderboards/${quizId}/allTime/${currentUser.uid}`);
                const leaderboardSnapshot = await get(leaderboardRef);
                const currentLeaderboard = leaderboardSnapshot.val() || {bestScore: 0, totalPlayed: 0};

                await update(leaderboardRef, {
                    displayName: currentUser.displayName,
                    bestScore: Math.max(currentLeaderboard.bestScore, quizResults.score),
                    totalPlayed: currentLeaderboard.totalPlayed + 1,
                    lastPlayed: serverTimestamp()
                });

                // Mettre à jour le classement mensuel
                const currentDate = new Date();
                const yearMonth = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;

                const monthlyLeaderboardRef = ref(database, `leaderboards/${quizId}/monthly/${yearMonth}/${currentUser.uid}`);
                const monthlyLeaderboardSnapshot = await get(monthlyLeaderboardRef);
                const currentMonthlyLeaderboard = monthlyLeaderboardSnapshot.val() || {bestScore: 0, gamesPlayed: 0};

                await update(monthlyLeaderboardRef, {
                    displayName: currentUser.displayName,
                    bestScore: Math.max(currentMonthlyLeaderboard.bestScore, quizResults.score),
                    gamesPlayed: currentMonthlyLeaderboard.gamesPlayed + 1
                });
            }

            // Rediriger vers la page de résultats
            navigate(isTrainingMode
                ? `/training/results/${gameSessionId}`
                : `/quiz/${quizId}/results/${gameSessionId}`
            );

        } catch (error) {
            console.error("Erreur lors de la finalisation du quiz:", error);
        }
    };

    // Rendu du composant de compte à rebours
    const renderer = ({minutes, seconds, completed}) => {
        if (completed) {
            return <span className="text-red-600">Temps écoulé !</span>;
        } else {
            return (
                <span className={`${minutes === 0 && seconds <= 10 ? 'text-red-600 animate-pulse' : ''}`}>
          {minutes}:{seconds < 10 ? `0${seconds}` : seconds}
        </span>
            );
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-indigo-100">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-indigo-100">
                <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
                    <div className="text-center">
                        <svg className="mx-auto h-12 w-12 text-red-500" fill="none" viewBox="0 0 24 24"
                             stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                        </svg>
                        <h3 className="mt-2 text-lg font-medium text-gray-900">Erreur</h3>
                        <p className="mt-1 text-sm text-gray-500">{error}</p>
                        <div className="mt-6">
                            <button
                                onClick={() => navigate(-1)}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                                Retour
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const currentQuestion = questions[currentQuestionIndex];

    if (!currentQuestion) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-indigo-100">
                <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
                    <div className="text-center">
                        <svg className="mx-auto h-12 w-12 text-yellow-500" fill="none" viewBox="0 0 24 24"
                             stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                        </svg>
                        <h3 className="mt-2 text-lg font-medium text-gray-900">Aucune question disponible</h3>
                        <div className="mt-6">
                            <button
                                onClick={() => navigate(-1)}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                                Retour
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-purple-100 pb-12">

            <Header title={quiz?.metadata?.title || 'Quiz Biblique'} showBackButton={true}/>

            <main className="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                <div className="bg-white shadow-lg rounded-lg overflow-hidden">
                    {/* Barre de progression */}
                    <div className="relative h-2 bg-gray-200">
                        <div
                            className="absolute h-2 bg-indigo-600 transition-all duration-500"
                            style={{width: `${((currentQuestionIndex) / questions.length) * 100}%`}}
                        ></div>
                    </div>

                    {/* Chronomètre */}
                    <div className="flex justify-between items-center px-6 py-3 bg-indigo-100">
                        <div className="flex items-center">
                            <svg className="h-5 w-5 text-indigo-600 mr-2" fill="none" viewBox="0 0 24 24"
                                 stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                            </svg>
                            <div className="text-sm font-medium">
                                {timerRunning ? (
                                    <Countdown
                                        key={countdownKey}
                                        date={Date.now() + timeRemaining}
                                        renderer={renderer}
                                        onComplete={handleTimerComplete}
                                    />
                                ) : (
                                    <span>Temps écoulé</span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Question */}
                    <div className="px-6 py-6 border-b border-gray-200">
                        <h2 className="text-2xl font-bold text-gray-800 mb-4">{currentQuestion.text}</h2>

                        {/* Options */}
                        <div className="space-y-4">
                            {currentQuestion.options.map((option, index) => (
                                <button
                                    key={index}
                                    className={`w-full py-4 px-6 rounded-lg text-left transition focus:outline-none ${
                                        selectedOption === index && !showExplanation
                                            ? 'bg-indigo-100 border-2 border-indigo-500'
                                            : showExplanation
                                                ? index === currentQuestion.correctAnswer
                                                    ? 'bg-green-100 border-2 border-green-500'
                                                    : selectedOption === index
                                                        ? 'bg-red-100 border-2 border-red-500'
                                                        : 'bg-gray-50 border-2 border-gray-200 hover:bg-gray-100'
                                                : 'bg-gray-50 border-2 border-gray-200 hover:bg-gray-100'
                                    }`}
                                    onClick={() => {
                                        if (!showExplanation && timerRunning) {
                                            setSelectedOption(index);
                                        }
                                    }}
                                    disabled={showExplanation || !timerRunning}
                                >
                                    <div className="flex items-start">
                                        <div
                                            className={`flex-shrink-0 h-6 w-6 rounded-full flex items-center justify-center mr-3 ${
                                                showExplanation
                                                    ? index === currentQuestion.correctAnswer
                                                        ? 'bg-green-500 text-white'
                                                        : selectedOption === index
                                                            ? 'bg-red-500 text-white'
                                                            : 'bg-gray-300 text-white'
                                                    : 'bg-indigo-100 text-indigo-600'
                                            }`}>
                                            {['A', 'B', 'C', 'D'][index]}
                                        </div>
                                        <p className="text-gray-800">{option}</p>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Explication */}
                    {showExplanation && isTrainingMode && (
                        <div className="px-6 py-4 bg-indigo-50">
                            <div className="flex items-start">
                                <div
                                    className={`flex-shrink-0 h-6 w-6 rounded-full flex items-center justify-center mr-3 ${
                                        selectedOption === currentQuestion.correctAnswer
                                            ? 'bg-green-500 text-white'
                                            : 'bg-red-500 text-white'
                                    }`}>
                                    {selectedOption === currentQuestion.correctAnswer ? '✓' : '✗'}
                                </div>
                                <div>
                                    <h3 className={`font-medium ${
                                        selectedOption === currentQuestion.correctAnswer
                                            ? 'text-green-800'
                                            : 'text-red-800'
                                    }`}>
                                        {selectedOption === currentQuestion.correctAnswer
                                            ? 'Bonne réponse !'
                                            : 'Mauvaise réponse'
                                        }
                                    </h3>
                                    <p className="text-gray-600 mt-1">
                                        <span className="font-medium">Référence :</span> {currentQuestion.verse}
                                    </p>
                                    {currentQuestion.explanation && (
                                        <p className="text-gray-600 mt-2">
                                            {currentQuestion.explanation}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Bouton de validation */}
                    <div className="px-6 py-4 bg-gray-50 flex justify-between">
                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                            Abandonner
                        </button>
                        <button
                            type="button"
                            onClick={handleAnswerSubmit}
                            disabled={selectedOption === null && !showExplanation}
                            className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
                                selectedOption === null && !showExplanation
                                    ? 'bg-indigo-400 cursor-not-allowed'
                                    : 'bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                            }`}
                        >
                            {showExplanation
                                ? currentQuestionIndex < questions.length - 1
                                    ? 'Question suivante'
                                    : 'Terminer le quiz'
                                : 'Valider réponse'
                            }
                        </button>
                    </div>
                </div>
            </main>
        </div>
    );
}
